<template>
  <div class="annotation-credits">
    <div class="annotation-credits__header">
      <div class="annotation-credits__header-content">
        <div
          data-test="plan-name"
          class="annotation-credits__header-content-title"
        >
          {{ planName }}
          <ToolTip
            :content="lockedOutReasons?.join(', ')"
            :disabled="!isLockedOut"
          >
            <Badge
              data-test="plan-status"
              :label="isLockedOut ? 'Inactive' : 'Active'"
              size="small"
              no-tooltip
              :color="isLockedOut ? RED_COLOR : GREEN_COLOR"
            />
          </ToolTip>
        </div>
        <div
          v-if="isClient"
          class="annotation-credits__header-content-partner-name"
        >
          Credit availability is managed by your workspace admin.
        </div>
        <div
          v-if="formattedRenewDate"
          class="annotation-credits__header-content-date"
          data-test="plan-renew-date"
        >
          Renews on
          <span class="annotation-credits__header-content-date-name">{{ formattedRenewDate }}</span>
        </div>
      </div>
      <div
        v-if="!isClient"
        class="annotation-credits__header-actions"
      >
        <DarwinButton
          size="xs"
          kind="primary"
          flair="rounded"
          @click="contactSales"
        >
          Upgrade
        </DarwinButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIntercom } from '@/composables/useIntercom'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Badge from '@/uiKit/Badge/Badge.vue'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import type { RGBA } from '@/uiKit/colorPalette'

const GREEN_COLOR: RGBA = { r: 22, g: 163, b: 74, a: 1 }
const RED_COLOR: RGBA = { r: 220, g: 24, b: 24, a: 1 }

defineProps<{
  planName: string | null
  formattedRenewDate: string | null
  isClient?: boolean
  isLockedOut?: boolean
  lockedOutReasons?: string[]
}>()

const { intercom } = useIntercom()

const contactSales = (): void => {
  if (!intercom) {
    return
  }

  intercom.trackEvent('upgrade_request')
  const message =
    "Hi V7 Team, we'd like to explore upgrading our plan." + ' Looking forward to hearing from you.'
  intercom.showNewMessage(message)
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';
@import '@/assets/styles/shared/scrollbar';

.annotation-credits {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  gap: var(--size-6);

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    &-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      flex-grow: 1;
      gap: var(--size-1);
      width: 100%;

      &-title {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: var(--size-1);
        text-transform: capitalize;
        @include fontRegularBody400;
        color: var(--color-text-default);
        font-weight: 600;
      }

      &-partner-name {
        @include fontRegularBody100;
        color: var(--color-text-subtlest);

        &-strong {
          font-weight: 600;
          color: var(--color-text-subtle);
        }
      }

      &-date {
        @include fontRegularBody100;
        color: var(--color-text-subtlest);
      }
    }

    &-actions {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-start;
    }
  }
}
</style>
